import { Dispatch, SetStateAction } from "react";
import { CloseTemplateBasis } from "@common/types/closeTemplateBasis";
import type { OrganizationTaskTemplate } from "@common/types/organizationTaskTemplate";
import { TaskType } from "@common/types/task";
import { Picker } from "@design-system/Picker/Picker.lib";
import type { PickerItemData } from "@design-system/Picker/Picker.types";
import { DescriptionListItem } from "@design-system/DescriptionList/DescriptionList";
import taskStyles from "@components/drawers/TaskDrawerFields.module.scss";
import { PeriodDateFields } from "./PeriodDateFields";
import { DocumentRetrievalFields } from "./DocumentRetrievalFields";
import { ReconciliationFields } from "./ReconciliationFields";
import { ReviewFields } from "./ReviewFields";
import { TaskTypePicker } from "./TaskTypePicker";
import {
    TRANSACTION_PRESET_UNCATEGORIZED,
    resolveTaskTypeFromOption,
    TRANSACTIONS_VIEW_TYPE_OPTIONS,
    type TaskTypeOption,
} from "./closeTemplateTasksDrawer.constants";
import { type TransactionsViewType } from "./closeTemplateTaskViewConfig";

const TRANSACTIONS_VIEW_TYPE_ITEMS: Array<
    PickerItemData<TransactionsViewType>
> = TRANSACTIONS_VIEW_TYPE_OPTIONS.map(({ value, label }) => ({
    value,
    label,
}));

interface TaskTypeFieldProps {
    htmlIdPrefix: string;
    taskType: TaskTypeOption;
    viewConfig: Record<string, unknown> | null;
    onTaskTypeChange: (option: TaskTypeOption) => void;
    onViewConfigChange: Dispatch<
        SetStateAction<Record<string, unknown> | null>
    >;
    basis: CloseTemplateBasis | null;
    transactionsViewType: TransactionsViewType;
    onTransactionsViewTypeChange: (viewType: TransactionsViewType) => void;
    orgTaskTemplates?: readonly OrganizationTaskTemplate[];
    onOrgTaskTemplateSelect?: (template: OrganizationTaskTemplate) => void;
}

export const TaskTypeField = ({
    htmlIdPrefix,
    taskType,
    viewConfig,
    onTaskTypeChange,
    onViewConfigChange,
    basis,
    transactionsViewType,
    onTransactionsViewTypeChange,
    orgTaskTemplates,
    onOrgTaskTemplateSelect,
}: TaskTypeFieldProps) => {
    const isTransactionReviewSelected =
        taskType === TaskType.TRANSACTION_REVIEW ||
        taskType === TRANSACTION_PRESET_UNCATEGORIZED;

    return (
        <>
            <DescriptionListItem label="Action">
                <div className={taskStyles.detailSelectWrapper}>
                    <TaskTypePicker
                        taskType={taskType}
                        viewConfig={viewConfig}
                        onTaskTypeChange={onTaskTypeChange}
                        onViewConfigChange={onViewConfigChange}
                        basis={basis}
                        orgTaskTemplates={orgTaskTemplates}
                        onOrgTaskTemplateSelect={onOrgTaskTemplateSelect}
                        triggerClassName={taskStyles.detailSelectTrigger}
                    />
                </div>
            </DescriptionListItem>

            {resolveTaskTypeFromOption(taskType) === TaskType.TRANSACTIONS && (
                <DescriptionListItem label="View type">
                    <div className={taskStyles.detailSelectWrapper}>
                        <Picker.Select
                            items={TRANSACTIONS_VIEW_TYPE_ITEMS}
                            value={transactionsViewType}
                            onValueChange={(value) => {
                                if (value) {
                                    onTransactionsViewTypeChange(value);
                                }
                            }}
                            trigger={
                                <Picker.Button
                                    variant="primary"
                                    className={taskStyles.detailSelectTrigger}
                                />
                            }
                        />
                    </div>
                </DescriptionListItem>
            )}

            {(taskType === TaskType.PROFIT_LOSS ||
                taskType === TaskType.BALANCE_SHEET) && (
                <PeriodDateFields
                    htmlIdPrefix={htmlIdPrefix}
                    taskType={
                        taskType === TaskType.BALANCE_SHEET
                            ? TaskType.BALANCE_SHEET
                            : TaskType.PROFIT_LOSS
                    }
                    viewConfig={viewConfig}
                    onViewConfigChange={onViewConfigChange}
                />
            )}

            {taskType === TaskType.DOCUMENT_RETRIEVAL && (
                <DocumentRetrievalFields
                    htmlIdPrefix={htmlIdPrefix}
                    viewConfig={viewConfig}
                    onViewConfigChange={onViewConfigChange}
                />
            )}

            {taskType === TaskType.RECONCILIATION && (
                <ReconciliationFields
                    viewConfig={viewConfig}
                    onViewConfigChange={onViewConfigChange}
                />
            )}

            {isTransactionReviewSelected && (
                <ReviewFields
                    viewConfig={viewConfig}
                    onViewConfigChange={onViewConfigChange}
                    basis={basis}
                />
            )}
        </>
    );
};
